export type EvidenceLevel = "observed" | "computed" | "client_reported" | "inferred";

export type Meeting = {
  id: string;
  date: string;
  shortDate: string;
  title: string;
  phase: string;
  duration: string;
  confidence: "High" | "Medium-high" | "Medium";
  summary: string;
  outcome: string;
  decisions: string[];
  clientActions: string[];
  automatrixActions: string[];
  signals: { label: string; value: string; level: EvidenceLevel }[];
  platforms: string[];
  recordings: { title: string; url: string }[];
  notes: { language: string; url: string }[];
};

export const clientProfile = {
  name: "Alessandro Gatti",
  handle: "@alessandrogatticoaching",
  company: "AG Coaching",
  offer: "InShape360",
  audience: "Women 30–50 · home training · cellulite · water retention",
  classification: "ICP-A · Ideal Client",
  demand: "Automation + AI Agent + FDE",
  initialDemand: "Custom fitness application",
  stage: "Access & implementation planning",
  email: "alexg***@gmail.com",
  phone: "+39 333 *** 9570",
  channel: "WhatsApp",
  language: "English / Italian",
  timezone: "Europe/Rome",
  owner: "Lucas · Automatrix",
};

export const clientInvoice = {
  id: "in_1U82JpCSqil72U7W03OThdlG",
  number: "JNNH6QYO-0001",
  status: "Open",
  amount: "€200.00",
  description: "AG Coaching — Initial partnership payment",
  issuedAt: "2026-08-24",
  dueAt: "2026-09-23",
  hostedUrl: "#",
  pdfUrl: "#",
};

export const operatingMetrics = [
  { label: "Audience", value: "~175K", note: "Instagram followers", level: "client_reported" as const },
  { label: "Welcome outreach", value: "~150/day", note: "manual DMs by setter", level: "client_reported" as const },
  { label: "Reply rate", value: "10–15%", note: "welcome-message baseline", level: "client_reported" as const },
  { label: "Appointments", value: "1–2/day", note: "from welcome outreach", level: "client_reported" as const },
  { label: "Follower cost", value: "€0.20–0.40", note: "paid follower acquisition", level: "client_reported" as const },
  { label: "Current shows", value: "3–4/day", note: "reported in call 03", level: "client_reported" as const },
  { label: "Show target", value: "10/day", note: "from ~14 bookings", level: "client_reported" as const },
  { label: "Revenue target", value: "€40K+/mo", note: "brief scenario", level: "client_reported" as const },
];

export const workstreams = [
  { id: "01", title: "Content engine", status: "Priority 01", progress: 20, text: "AI-assisted ideation, production calendar and approved multi-channel distribution across Instagram, Facebook, TikTok and YouTube." },
  { id: "02", title: "Instagram conversation agent", status: "Priority 01", progress: 15, text: "Welcome new followers, classify replies, route leads into Skool or booking and log the full conversation state in GoHighLevel." },
  { id: "03", title: "Skool series funnel", status: "Confirmed", progress: 30, text: "Free community with five episodic lessons, keyword CTAs, YouTube reuse and the InShape360 premium offer after episode five." },
  { id: "04", title: "Measurement & CRM", status: "Access required", progress: 10, text: "Unify Meta, Instagram, GoHighLevel, Sheets and sales outcomes into follower → DM → booked → show → sale reporting." },
  { id: "05", title: "Paid acquisition", status: "After instrumentation", progress: 5, text: "Audit follower and retargeting campaigns, then test cold traffic to Skool plus VSL and quiz Plan B funnels." },
  { id: "06", title: "Fitness application", status: "Phase 02", progress: 8, text: "Proprietary training and nutrition application with rule-constrained generation and a generic female 3D exercise avatar." },
];

export const accessRegister = [
  { system: "Meta Business Manager", purpose: "Ads, audiences, campaign and attribution data", status: "Required" },
  { system: "Instagram Professional", purpose: "Messages, insights, comments and follower events", status: "Required" },
  { system: "GoHighLevel", purpose: "Contacts, conversations, pipelines, calendars and outcomes", status: "Required" },
  { system: "WAGHL", purpose: "WhatsApp connection used inside GoHighLevel", status: "Observed" },
  { system: "Skool", purpose: "Community, members, series consumption and conversion", status: "Required" },
  { system: "Publishing platform", purpose: "Current content scheduling and distribution workflow", status: "Identify" },
  { system: "Google Workspace", purpose: "Calls, recordings, notes, Sheets and Drive", status: "Automatrix connected" },
  { system: "EvolutionFitPro", purpose: "Current coaching delivery and future migration reference", status: "Observed" },
];

export const infrastructure = [
  { name: "VPS", state: "Planned", detail: "Dedicated, secured client runtime" },
  { name: "Claude", state: "Planned", detail: "Analysis, content and operating intelligence" },
  { name: "Codex", state: "Planned", detail: "FDE engineering and maintenance" },
  { name: "n8n", state: "Planned", detail: "Workflow orchestration and event routing" },
  { name: "GoHighLevel MCP / CLI", state: "Access", detail: "CRM discovery, actions and observability" },
  { name: "Portainer", state: "Planned", detail: "Container administration and audit" },
  { name: "Supabase", state: "Active", detail: "Auth, private intelligence data and storage" },
  { name: "Cloudflare", state: "Active", detail: "Portal, edge functions and delivery" },
];

export const meetings: Meeting[] = [
  {
    id: "call-01",
    date: "2026-08-17",
    shortDate: "17 AUG",
    title: "Application discovery & avatar scope",
    phase: "Demand discovery",
    duration: "45 min",
    confidence: "High",
    summary: "The engagement began as a proprietary training and nutrition application. Alessandro described the limitations of EvolutionFitPro and the need to encode his methodology without allowing an AI model to invent programs.",
    outcome: "Lead generation and nurturing were moved ahead of the application. The app remains a second-track product after commercial infrastructure is operating.",
    decisions: [
      "Start any app v1 with a generic female 3D avatar library.",
      "Treat Alessandro likeness or custom video as a later production phase.",
      "Use his coaching rules as constraints around generation and keep human approval.",
      "Prioritize acquisition economics before expanding product scope.",
    ],
    clientActions: ["Provide methodology rules and training/nutrition materials.", "Confirm commercial model and channel permissions."],
    automatrixActions: ["Map the first application architecture.", "Audit the acquisition operation and economics."],
    signals: [
      { label: "Manual outreach", value: "~150 welcome DMs/day", level: "client_reported" },
      { label: "Team", value: "2 sellers · 2 coaches", level: "client_reported" },
      { label: "Initial demand", value: "Application", level: "observed" },
    ],
    platforms: ["EvolutionFitPro", "Instagram", "Meta Ads", "Google Meet"],
    recordings: [
      { title: "Recording · part 01", url: "https://drive.google.com/file/d/1-1ro3Eqk5WTGsX9GnWiywaBX6cKiRrF4/view" },
      { title: "Recording · part 02", url: "https://drive.google.com/file/d/1t2AlnfXpBGDDxJVZA3kAJQfqA5N0RevK/view" },
    ],
    notes: [
      { language: "Gemini notes · English", url: "https://docs.google.com/document/d/1u7C_xO9ZRfPPfwDvP4GHqtYuDIGpOtPkAgbV2kJ4wps/edit" },
      { language: "Gemini notes · Portuguese", url: "https://docs.google.com/document/d/13vhAaE8BIiaiGzBPAcObvxahkbfIt_VmqN8mC-g8oM8/edit" },
    ],
  },
  {
    id: "call-02",
    date: "2026-08-19",
    shortDate: "19 AUG",
    title: "Architecture, FDE & commercial offer",
    phase: "Solution design",
    duration: "71 min",
    confidence: "Medium",
    summary: "The project shifted from a one-time application build toward a continuous Forward Deployed Engineer model connecting data, integrations, models and commercial operations.",
    outcome: "Operation 60K became the commercial north star: use the existing audience and AI-assisted social selling to grow revenue while reducing dependence on paid acquisition.",
    decisions: [
      "Operate with an ongoing FDE rather than a single handoff.",
      "Use the B2B coaching operation to finance a later B2C application.",
      "Launch a free educational community with a premium coaching path.",
      "Prepare competitor research, wireframes and an Instagram automation example.",
    ],
    clientActions: ["Provide operating materials.", "Validate partnership and implementation investment."],
    automatrixActions: ["Prepare market research and wireframe.", "Draft the infrastructure and delivery proposal."],
    signals: [
      { label: "Audience", value: "160–175K followers", level: "client_reported" },
      { label: "Story reach", value: "1.5–1.8K/day", level: "client_reported" },
      { label: "North star", value: "+€60K in four months", level: "client_reported" },
    ],
    platforms: ["Instagram", "Meta Ads", "FDE", "Web/iOS", "AI APIs"],
    recordings: [{ title: "Full recording", url: "https://drive.google.com/file/d/18C8RBAfqpCie13qshyebJV7tFzoVTbyu/view" }],
    notes: [
      { language: "Gemini notes · English", url: "https://docs.google.com/document/d/1x4sHTzqrkYV3FwyLGaW4nSDvQtCMqyJvratbbniQVhk/edit" },
      { language: "Gemini notes · Portuguese", url: "https://docs.google.com/document/d/17NKewW_7xsCai-QyRngXe4GO8Rj4BlYiPIoSPuWJ8Rc/edit" },
    ],
  },
  {
    id: "call-03",
    date: "2026-08-21",
    shortDate: "21 AUG",
    title: "Commercial targets & show-up system",
    phase: "Revenue model",
    duration: "45 min",
    confidence: "Medium",
    summary: "The commercial conversation focused on moving from roughly three to four daily show-ups toward ten, while using AI to supervise repetitive Instagram nurturing and preserve human intervention for qualified leads.",
    outcome: "The immediate product became a measurable B2B growth system: content, conversation, qualification, booking, reminders, show-up and sale attribution.",
    decisions: [
      "Make the setter a supervisor/reviewer of AI-assisted outreach.",
      "Connect Instagram conversation state to GoHighLevel.",
      "Segment funnels by demographic and content performance.",
      "Improve confirmation and follow-up before increasing acquisition volume.",
    ],
    clientActions: ["Process the initial partnership payment.", "Complete onboarding and organize access."],
    automatrixActions: ["Send onboarding link.", "Train the content creator and organize the working group."],
    signals: [
      { label: "Current shows", value: "3–4/day", level: "client_reported" },
      { label: "Target shows", value: "10/day", level: "client_reported" },
      { label: "Target cost/show", value: "€50–70", level: "client_reported" },
      { label: "Close rate", value: "~50% when shown", level: "client_reported" },
    ],
    platforms: ["Instagram", "GoHighLevel", "Meta Ads", "AI video"],
    recordings: [{ title: "Full recording", url: "https://drive.google.com/file/d/1YJlknHaYxcKuKkMMkp5EIIAs5BdzATCb/view" }],
    notes: [
      { language: "Gemini notes · English", url: "https://docs.google.com/document/d/1vcR6yp01uf6cP6QPm2KqEzmQK90uy8N50CdDdeGSWXQ/edit" },
      { language: "Gemini notes · Portuguese", url: "https://docs.google.com/document/d/18kdR4Nw19mZqWliKEeLCLPfAJ7Wx5G00C8obp2drhj0/edit" },
    ],
  },
  {
    id: "call-04",
    date: "2026-08-22",
    shortDate: "22 AUG",
    title: "Onboarding, channels & operating dashboard",
    phase: "Operational onboarding",
    duration: "32 min",
    confidence: "Medium-high",
    summary: "Alessandro demonstrated Instagram, Meta campaign reporting, GoHighLevel with WAGHL, a publishing workflow and a Google Sheets operating dashboard. The call confirmed the real systems the FDE must connect.",
    outcome: "First implementation sequence: provision the client environment, connect content distribution and Instagram response operations, route leads toward Skool, then instrument Meta and expand paid funnels.",
    decisions: [
      "Use GoHighLevel as the operational CRM.",
      "Keep WAGHL as the observed WhatsApp bridge until its access and limits are audited.",
      "Combine daily revenue, expense and EBITDA metrics with funnel events.",
      "Start with content plus Instagram response automation before deeper ad experiments.",
    ],
    clientActions: ["Provide Meta, Instagram, GHL, WAGHL, Skool and publishing access.", "Confirm the active WhatsApp number/sub-account."],
    automatrixActions: ["Prepare the implementation action list.", "Create dashboards and integration map.", "Schedule the follow-up."],
    signals: [
      { label: "Viral post", value: "~60K views · +140 followers", level: "client_reported" },
      { label: "WAGHL", value: "~US$25/month", level: "client_reported" },
      { label: "CRM role", value: "Agency Owner", level: "observed" },
    ],
    platforms: ["GoHighLevel", "WAGHL", "ManyChat", "Meta", "Instagram", "Google Sheets", "Drive", "HeyGen", "Lovable"],
    recordings: [{ title: "Full recording", url: "https://drive.google.com/file/d/1T6HAE6GpEH1IdSporv42gVytwMHX5All/view" }],
    notes: [
      { language: "Gemini notes · English", url: "https://docs.google.com/document/d/1SoEmvM212PKgslsYhHf0_YNl-LtlF0vVhCQ9Vmsm4zg/edit" },
      { language: "Gemini notes · Portuguese", url: "https://docs.google.com/document/d/1MsrMjkqDAMOqSC4IHByhmZTOQ14jAuEcXZOLK281BMg/edit" },
    ],
  },
];

export const knowledgeDocuments = [
  { type: "CLIENT BRIEF", title: "Brief AG Coaching Strategy", state: "Primary source", description: "Current acquisition strategy, planned Skool series, AI-agent demand, capacity model and revenue expectations.", url: "https://docs.google.com/document/d/1QOWeV6GY4l2JRwJ3i08FTiFNt5-GeAtKDR-D6I-Gnp8/edit" },
  { type: "CALL SYNTHESIS", title: "Four-call consolidated report", state: "Internal analysis", description: "Classification, evolution of demand, decisions, metrics, stack, risks and commitments across the four calls.", url: "https://github.com/Morfeu333/2026-08-22-alessandro-gatti-onboarding-analysis" },
  { type: "VIDEO WALKTHROUGH", title: "22 August onboarding analysis", state: "Frame verified", description: "Timestamped inspection of Instagram, Meta, GoHighLevel, WAGHL, reporting sheets and operating dashboards.", url: "https://github.com/Morfeu333/2026-08-22-alessandro-gatti-onboarding-analysis" },
  { type: "COMPETITOR REPORT", title: "Instagram competitor intelligence", state: "449 posts", description: "Quantitative benchmarks, 14 video forensics, comment analysis, funnel mapping and recommendations.", url: "#competitor-intelligence" },
  { type: "PIPELINE", title: "Automatrix lead-to-client pipeline", state: "Internal playbook", description: "Lead classification, contact requirements, discovery calls, proposal and implementation stages.", url: "#delivery-checklist" },
];

export const evidenceLegend: { level: EvidenceLevel; label: string; description: string }[] = [
  { level: "observed", label: "Observed", description: "Directly visible in a source platform, document, frame or recording." },
  { level: "computed", label: "Computed", description: "Calculated from collected records with reproducible logic." },
  { level: "client_reported", label: "Client reported", description: "Provided by Alessandro and awaiting source-system reconciliation." },
  { level: "inferred", label: "Model inferred", description: "Analytical interpretation, explicitly separated from observed evidence." },
];
